import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const ECOSICLE_INFO = {
  QT: { name: '問いの種', color: 'bg-purple-100 text-purple-700', icon: '🌱', emoji: '🌍➡🌱', image: '/badges/badge_QT.png' },
  DT: { name: '議論の樹幹', color: 'bg-blue-100 text-blue-700', icon: '🌳', emoji: '🌱➡🌳', image: '/badges/badge_DT.png' },
  TT: { name: '信任の花', color: 'bg-green-100 text-green-700', icon: '🌸', emoji: '🌳➡🌸', image: '/badges/badge_TT.png' },
  IT: { name: '招待の果実', color: 'bg-cyan-100 text-cyan-700', icon: '🍎', emoji: '🌸➡🍎', image: '/badges/badge_IT.png' },
  XT: { name: '実行の活力', color: 'bg-indigo-100 text-indigo-700', icon: '🦌', emoji: '🍎➡🦌', image: '/badges/badge_XT.png' },
  CT: { name: '貢献の代謝', color: 'bg-amber-100 text-amber-700', icon: '🫚', emoji: '🦌➡🫚', image: '/badges/badge_CT.png' },
  RT: { name: '振り返りの菌', color: 'bg-emerald-100 text-emerald-700', icon: '🍄', emoji: '🫚➡🍄', image: '/badges/badge_RT.png' },
  ET: { name: '共鳴の土壌', color: 'bg-pink-100 text-pink-700', icon: '🌍', emoji: '🍄➡🌍', image: '/badges/badge_ET.png' },
};

const BADGE_TIERS = {
  bronze: { label: 'ブロンズ', icon: '🥉', boost: '+5%', color: 'text-orange-600' },
  silver: { label: 'シルバー', icon: '🥈', boost: '+10%', color: 'text-gray-400' },
  gold: { label: 'ゴールド', icon: '🥇', boost: '+15%', color: 'text-yellow-500' },
};

const TIER_THRESHOLDS = {
  bronze: 10,
  silver: 50,
  gold: 300,
};

const EcoscicleBadgeDisplay = ({ actorId, compact = false }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadges = async () => {
      if (!actorId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/badges/ecosicle/${actorId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        setBadges(response.data.badges || []);
      } catch (error) {
        console.error('バッジ取得エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBadges();
  }, [actorId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        バッジなし
      </div>
    );
  }

  const getProgressToNextTier = (tokenType, currentPoints) => {
    const currentBadge = badges.find((b) => b.token_type === tokenType);
    const currentTier = currentBadge?.badge_tier;

    if (currentTier === 'gold') {
      return { nextTier: null, progress: 100, remaining: 0 };
    }

    const nextTier = currentTier === 'silver' ? 'gold' : currentTier === 'bronze' ? 'silver' : 'bronze';
    const nextThreshold = TIER_THRESHOLDS[nextTier];
    const prevThreshold = currentTier ? TIER_THRESHOLDS[currentTier] : 0;

    const progress = Math.min(
      100,
      ((currentPoints - prevThreshold) / (nextThreshold - prevThreshold)) * 100
    );
    const remaining = Math.max(0, nextThreshold - currentPoints);

    return { nextTier, progress, remaining };
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {badges.map((badge) => {
          const ecosicle = ECOSICLE_INFO[badge.token_type];
          const tier = BADGE_TIERS[badge.badge_tier];
          if (!tier) return null;

          return (
            <div
              key={badge.token_type}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs"
              title={`${ecosicle.name} - ${tier.label} (${badge.total_points}pt)`}
            >
              <img src={ecosicle.image} alt={ecosicle.name} className="w-5 h-5" />
              <span className={tier.color}>{tier.icon}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
        🎖️ エコサイクルバッジ
        <span className="text-xs text-gray-500 font-normal">
          （マイニングブースト効果あり）
        </span>
      </h3>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(ECOSICLE_INFO).map(([tokenType, ecosicle]) => {
          const badge = badges.find((b) => b.token_type === tokenType);
          const points = badge?.total_points || 0;
          const tier = badge?.badge_tier;
          const tierInfo = tier ? BADGE_TIERS[tier] : null;
          const { nextTier, progress } = getProgressToNextTier(
            tokenType,
            points
          );

          const targetTier = tier || 'bronze';
          const targetThreshold = TIER_THRESHOLDS[targetTier];
          const fillPercentage = Math.min((points / targetThreshold) * 100, 100);

          return (
            <div
              key={tokenType}
              className={`p-3 rounded-lg border-2 ${
                tierInfo ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
              } relative overflow-hidden`}
            >
              <div
                className={`absolute inset-0 transition-all duration-500 ${
                  tierInfo
                    ? tierInfo.color === 'text-yellow-500'
                      ? 'bg-yellow-100'
                      : tierInfo.color === 'text-gray-400'
                        ? 'bg-gray-100'
                        : 'bg-orange-100'
                    : 'bg-gray-100'
                }`}
                style={{
                  clipPath: `circle(${fillPercentage}% at center)`,
                  opacity: tierInfo ? 0.3 : 0.1,
                }}
              ></div>

              <div className="relative z-10">
                <div className="flex flex-col items-center text-center mb-2">
                  <img
                    src={ecosicle.image}
                    alt={ecosicle.name}
                    className={`w-16 h-16 mb-1 transition-all duration-300 ${
                      tierInfo ? 'opacity-100' : 'opacity-30'
                    }`}
                  />
                  <div className="text-xs font-semibold text-gray-700">
                    {ecosicle.name}
                  </div>
                  <div className="text-[10px] text-gray-500">{tokenType}</div>
                </div>

                {tierInfo ? (
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className={`text-xl ${tierInfo.color}`}>
                      {tierInfo.icon}
                    </span>
                    <span className="text-xs text-gray-600 font-medium">
                      {tierInfo.boost}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-gray-400 text-xl opacity-30">
                      🥉
                    </span>
                  </div>
                )}

                <div className="text-xs text-center text-gray-700 mb-1 font-medium">
                  {points}pt
                  {nextTier && (
                    <span className="text-gray-500 font-normal">
                      /{targetThreshold}pt
                    </span>
                  )}
                </div>

                {!tierInfo && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 relative overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${fillPercentage}%` }}
                      ></div>
                    </div>
                    <div className="text-[10px] text-gray-500 text-center mt-1">
                      {TIER_THRESHOLDS.bronze - points}pt で獲得
                    </div>
                  </div>
                )}

                {tierInfo && nextTier && (
                  <div className="mt-2">
                    <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="text-[10px] text-gray-500 text-center mt-1">
                      次の{BADGE_TIERS[nextTier].label}まで{' '}
                      {TIER_THRESHOLDS[nextTier] - points}pt
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

EcoscicleBadgeDisplay.propTypes = {
  actorId: PropTypes.number.isRequired,
  compact: PropTypes.bool,
};

export default EcoscicleBadgeDisplay;
